import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { TwitterLogo, YoutubeLogo, InstagramLogo, Globe, Users, Eye, LinkSimple, Stack, Lightning } from '@phosphor-icons/react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function PublicStorefront() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function load() {
      try {
        const res = await fetch(`${API_BASE}/api/storefronts/${slug}`);
        if (!res.ok) throw new Error('Storefront not found');
        setData(await res.json());
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [slug]);

  if (loading) return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
      <div className="text-zinc-400 animate-pulse">Loading storefront...</div>
    </div>
  );

  if (error || !data) return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
      <div className="text-center">
        <h1 className="text-4xl font-bold text-zinc-300 mb-2">404</h1>
        <p className="text-zinc-500">{error || 'Storefront not found'}</p>
      </div>
    </div>
  );

  const { profile, collections, featured_offers, followers } = data;
  const socials = profile?.social_links || {};

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100" data-testid="public-storefront">
      {/* Header */}
      <div className="relative">
        <div className="h-40 bg-gradient-to-r from-blue-900/40 via-purple-900/30 to-zinc-900" />
        <div className="max-w-4xl mx-auto px-4 -mt-14">
          <div className="flex items-end gap-4">
            <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-4xl font-bold text-white border-4 border-zinc-950 shrink-0">
              {profile?.display_name?.[0] || 'C'}
            </div>
            <div className="pb-2 flex-1 min-w-0">
              <h1 className="text-2xl font-bold flex items-center gap-2">
                {profile?.display_name}
                {profile?.verification_badge && <Badge className="bg-blue-600/20 text-blue-400 text-[10px]">Verified</Badge>}
              </h1>
              <div className="flex items-center gap-4 mt-1 text-sm text-zinc-400">
                <span className="flex items-center gap-1"><Users size={14} /> {followers || 0} followers</span>
                <span className="flex items-center gap-1"><Eye size={14} /> {profile?.total_views || 0} views</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8 space-y-8">
        {/* Bio & Social */}
        <div className="space-y-3">
          {profile?.bio && <p className="text-sm text-zinc-300 leading-relaxed">{profile.bio}</p>}
          <div className="flex flex-wrap gap-1.5">
            {profile?.categories?.map(c => (
              <span key={c} className="px-2 py-0.5 rounded text-[10px] bg-zinc-800 text-zinc-400 border border-zinc-700">{c}</span>
            ))}
          </div>
          <div className="flex items-center gap-4 text-zinc-500 text-sm">
            {socials.twitter && <a href={`https://twitter.com/${socials.twitter.replace('@','')}`} target="_blank" rel="noreferrer" className="hover:text-blue-400 transition-colors flex items-center gap-1"><TwitterLogo size={16} /> {socials.twitter}</a>}
            {socials.youtube && <a href={`https://youtube.com/@${socials.youtube}`} target="_blank" rel="noreferrer" className="hover:text-red-400 transition-colors flex items-center gap-1"><YoutubeLogo size={16} /> {socials.youtube}</a>}
            {socials.instagram && <a href={`https://instagram.com/${socials.instagram.replace('@','')}`} target="_blank" rel="noreferrer" className="hover:text-pink-400 transition-colors flex items-center gap-1"><InstagramLogo size={16} /> {socials.instagram}</a>}
            {socials.website && <a href={socials.website} target="_blank" rel="noreferrer" className="hover:text-zinc-300 transition-colors flex items-center gap-1"><Globe size={16} /> Website</a>}
          </div>
        </div>

        {/* Disclosure */}
        {profile?.disclosure_text && (
          <p className="text-[10px] text-zinc-600 border-l-2 border-zinc-800 pl-3">{profile.disclosure_text}</p>
        )}

        {/* Featured Offers */}
        {featured_offers?.length > 0 && (
          <div>
            <h2 className="text-lg font-semibold mb-3 flex items-center gap-2"><Lightning size={20} className="text-amber-400" /> Featured Offers</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {featured_offers.map(offer => (
                <Card key={offer.offer_id} className="bg-zinc-900/50 border-zinc-800 hover:border-zinc-700 transition-colors">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-medium text-zinc-200">{offer.title}</h3>
                        <p className="text-xs text-zinc-500 mt-1">{offer.description?.substring(0, 100)}</p>
                      </div>
                      <Badge className="bg-emerald-900/30 text-emerald-400 border-emerald-700/40 text-xs shrink-0 ml-2">${offer.payout}</Badge>
                    </div>
                    <div className="flex items-center gap-2 mt-2 text-[10px] text-zinc-600">
                      <span>{offer.type}</span>
                      <span>&middot;</span>
                      <span>{offer.category}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Collections with Items */}
        {collections?.length > 0 && (
          <div>
            <h2 className="text-lg font-semibold mb-3 flex items-center gap-2"><Stack size={20} className="text-emerald-400" /> Collections</h2>
            <div className="space-y-6">
              {collections.map(col => (
                <div key={col.collection_id}>
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="font-medium text-zinc-200">{col.name}</h3>
                    <span className="text-[10px] text-zinc-600 capitalize">{col.type?.replace('_', ' ')}</span>
                  </div>
                  {col.description && <p className="text-xs text-zinc-500 mb-3">{col.description}</p>}
                  {col.items?.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {col.items.map(item => (
                        <a key={item.item_id} href={item.url || '#'} target={item.url ? '_blank' : '_self'} rel="noreferrer"
                          className="flex items-start gap-3 p-3 rounded-lg bg-zinc-900/50 border border-zinc-800 hover:border-zinc-700 transition-colors group">
                          {item.image_url ? (
                            <img src={item.image_url} alt="" className="w-12 h-12 rounded-lg object-cover shrink-0" />
                          ) : (
                            <div className="w-12 h-12 rounded-lg bg-zinc-800 flex items-center justify-center shrink-0"><LinkSimple size={16} className="text-zinc-600" /></div>
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="text-sm font-medium text-zinc-200 group-hover:text-blue-400 transition-colors truncate">{item.title}</p>
                              {item.price && <span className="text-[10px] text-emerald-400 font-medium shrink-0">{item.price}</span>}
                            </div>
                            {item.description && <p className="text-xs text-zinc-500 mt-0.5 line-clamp-1">{item.description}</p>}
                          </div>
                        </a>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-zinc-600">No items in this collection yet.</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Smart Links */}
        {data.smart_links?.length > 0 && (
          <div>
            <h2 className="text-lg font-semibold mb-3 flex items-center gap-2"><LinkSimple size={20} className="text-blue-400" /> Links</h2>
            <div className="space-y-2">
              {data.smart_links.slice(0, 10).map(link => (
                <div key={link.link_id} className="flex items-center justify-between p-3 rounded-lg bg-zinc-800/30 border border-zinc-800">
                  <div>
                    <p className="text-sm text-zinc-200">{link.offer_title}</p>
                    <p className="text-[10px] text-zinc-600">{link.clicks || 0} clicks</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="text-center text-[10px] text-zinc-700 pt-8 border-t border-zinc-900">
          Powered by Dynamic World Creator Commerce Platform
        </div>
      </div>
    </div>
  );
}
